type RetryOptions = {
  /**
   * Number of retries before giving up. Default is 3.
   * @default 3
   */
  retries?: number;

  /**
   * Delay in milliseconds between retries. Default is 500ms.
   * @default 500
   */
  delayMs?: number;

  /**
   * Callback function to execute on each error. Receives the error and the attempt number.
   */
  onError?: (error: unknown, attempt: number) => void;

  /**
   * Condition function to determine if the operation should be retried.
   * Receives the error and returns a boolean. Default is always retry.
   * @default () => true
   */
  retryCondition?: (error: unknown) => boolean;
};

export async function retryAsync<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const {
    retries = 3,
    delayMs = 1000,
    onError,
    retryCondition = () => true,
  } = options;

  // lastError is used to capture the last error encountered during the retries.
  // If all retries fail, this error will be thrown to the caller.
  // This allows us to provide a meaningful error message that reflects the last failure,
  let lastError: unknown;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      onError?.(error, attempt);

      const shouldRetry = attempt < retries && retryCondition(error);
      if (!shouldRetry) break;

      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  throw lastError;
};
